import Button from "@/components/atoms/Button";
import CourseBetaModal from "@/components/organisms/Modals/CourseBetaModal";
import LoginModal from "@/components/organisms/Modals/LoginModal";
import { useAuth } from "@/contexts/AuthContext";
import { useModal } from "@/hooks/useModal";
import { Course } from "@/types/Course";
import { Lesson } from "@/types/Lesson";
import { api } from "@/utils/api";
import { getUploadUrl } from "@/utils/uploadUtils";
import { Tab } from "@headlessui/react";
import { BookOpenIcon, CalendarIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { DateTime } from "luxon";
import Image from "next/image";
import { useRouter } from "next/router";
import { Fragment, useEffect } from "react";

const product = {
  highlights: [
    "Programa desde el navegador",
    "Sin configuración",
    "Educación Híbrida",
    "Ejercicios únicos",
  ],
  imageSrc: "",
  imageAlt:
    "Sample of 30 icons with friendly and fun details in outline, filled, and brand color styles.",
};

interface CourseDetailsProps {
  course: Course;
}

const CourseDetails: React.FC<CourseDetailsProps> = ({ course }) => {
  const router = useRouter();
  const { user } = useAuth();
  const { openModal, closeModal } = useModal();
  const { attributes } = course;
  const lessons = attributes.lessons.data;

  const { mutate: startCourse } = api.course.start.useMutation();

  const onEnroll = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    if (user) {
      router.push(`/curso/${attributes.slug}`);
      startCourse({
        courseId: course.id,
        courseUUID: attributes.uuid,
        courseName: attributes.title,
      });
    } else {
      openModal({
        title: "Comenzar curso",
        subtitle: "Inicia sesión o regístrate para comenzar el curso",
        body: <LoginModal onLoginRedirect={`/curso/${attributes.slug}`} />,
      });
    }
  };

  const showBetaModal = () => {
    const seenBetaModal = Boolean(localStorage.getItem("seenBetaModal"));
    if (!seenBetaModal) {
      openModal({
        title: "¡Bienvenido al beta!",
        subtitle:
          "Estamos trabajando en nuevas funcionalidades para mejorar la experiencia de aprendizaje",
        body: <CourseBetaModal />,
      });
    }
  };

  useEffect(() => {
    showBetaModal();
  }, []);

  const publishedAt = DateTime.fromISO(attributes.publishedAt)
    .setLocale("es")
    .toFormat("dd LLLL yyyy");
  return (
    <div className="bg-white">
      <div className="mx-auto px-4 py-16 sm:px-6 sm:py-24 lg:max-w-7xl lg:px-8">
        {/* Product */}
        <div className="lg:grid lg:grid-cols-7 lg:grid-rows-1 lg:gap-x-8 lg:gap-y-10 xl:gap-x-16">
          {/* Product image */}
          <div className="lg:col-span-4 lg:row-end-1">
            <div className="aspect-w-4 aspect-h-3 overflow-hidden rounded-lg bg-gray-400">
              <Image
                src={getUploadUrl(attributes.image.data.attributes)}
                alt={attributes.image.data.attributes.alternativeText}
                width={attributes.image.data.attributes.width}
                height={attributes.image.data.attributes.height}
              />
            </div>
          </div>

          {/* Product details */}
          <div className="mx-auto mt-14 max-w-2xl sm:mt-16 lg:col-span-3 lg:row-span-2 lg:row-end-2 lg:mt-0 lg:max-w-none">
            <div className="flex flex-col-reverse">
              <div className="-mt-2">
                <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                  {attributes.title}
                </h1>

                <h2 id="information-heading" className="sr-only">
                  Información sobre el curso
                </h2>
                <div className="mt-2 flex items-center space-x-2">
                  <CalendarIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  <p className="text-sm text-gray-500">
                    <time dateTime={attributes.publishedAt}>{publishedAt}</time>
                  </p>
                </div>
              </div>
            </div>

            <p className="mt-6 text-gray-500">{attributes.description}</p>

            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <Button
                type="button"
                variant="solid"
                color="primary"
                size="lg"
                block
                onClick={onEnroll}
              >
                Comenzar Curso
              </Button>
            </div>

            <div className="mt-10 border-t border-gray-200 pt-10">
              <h3 className="text-sm font-medium text-gray-900">
                Cómo vas a aprender
              </h3>
              <div className="prose prose-sm mt-4 text-gray-500">
                <ul role="list">
                  {product.highlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-16 w-full max-w-2xl lg:col-span-4 lg:mt-0 lg:max-w-none">
            <Tab.Group as="div">
              <div className="border-b border-gray-200">
                <Tab.List className="-mb-px flex space-x-8">
                  <Tab
                    className={({ selected }) =>
                      classNames(
                        selected
                          ? "border-primary text-primary"
                          : "border-transparent text-gray-700 hover:border-gray-300 hover:text-gray-800",
                        "whitespace-nowrap border-b-2 py-6 text-sm font-medium"
                      )
                    }
                  >
                    Contenido
                  </Tab>
                </Tab.List>
              </div>
              <Tab.Panels as={Fragment}>
                <Tab.Panel className="-mb-10">
                  <h3 className="sr-only">Contenido</h3>
                  {lessons.map((lesson: Lesson, idx: number) => (
                    <div
                      key={lesson.id}
                      className="flex space-x-4 text-sm text-gray-500"
                    >
                      <div className="flex-none py-10">
                        <BookOpenIcon className="h-8 w-8" />
                      </div>
                      <div
                        className={classNames(
                          idx === 0 ? "" : "w-full border-t border-gray-200",
                          "py-10"
                        )}
                      >
                        <h3 className="text-lg font-medium text-gray-900">
                          {lesson.attributes.title}
                        </h3>
                        <p>{lesson.attributes.description}</p>
                      </div>
                    </div>
                  ))}
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetails;
